// src/components/shop/CommunityPostCard.js - UPDATED

import React, { useState } from 'react';
import styled from 'styled-components';
import { Heart, Store } from 'lucide-react';
import { incrementPostViews } from '../../firebase/firebaseService';

const PostCard = styled.div`
  background: ${props => props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
  
  @media (hover: hover) {
    &:hover {
      transform: translateY(-5px);
      border-color: ${props => props.theme?.colors?.accent};
    }
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 87%;
  background: ${props => `${props.theme?.colors?.background || '#000000'}80`};
  overflow: hidden;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  video {
    background: #000;
  }
`;

const CaptionBar = styled.div`
  height: 13%;
  padding: 0.4rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}70`};
  
  @media (min-width: 768px) {
    padding: 0.5rem 0.75rem;
    gap: 0.4rem;
  }
`;

const LikeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 20px;
`;

const LikeButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.isLiked ? 
    props.theme?.colors?.accent || '#800000' : 
    `${props.theme?.colors?.text}60`};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.3s ease;
  padding: 0.25rem;
  
  &:active {
    transform: scale(0.9);
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: ${props => props.isLiked ? 'currentColor' : 'none'};
  }
`;

const ShopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  min-height: 20px;
  
  .shop-pfp {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    overflow: hidden;
    background: ${props => props.theme?.colors?.accent};
    flex-shrink: 0;
    cursor: pointer;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  .shop-name {
    font-weight: 600;
    color: ${props => props.theme?.colors?.accent};
    flex-shrink: 0;
    cursor: pointer;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .caption {
    color: ${props => props.theme?.colors?.text};
    opacity: 0.9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }
`;

const CommunityPostCard = ({ 
  post, 
  theme, 
  onLikeClick, 
  onPostClick,
  isAuthenticated 
}) => {
  const [hasViewed, setHasViewed] = useState(false);
  
  React.useEffect(() => {
    if (!hasViewed && post.shopId && post.id) {
      incrementPostViews(post.shopId, post.id);
      setHasViewed(true);
    }
  }, [post.shopId, post.id, hasViewed]);
  
  const handleLikeClick = (e) => {
    e.stopPropagation();
    if (onLikeClick) {
      onLikeClick(post);
    }
  };
  
  const handleCardClick = () => {
    if (onPostClick) {
      onPostClick(post);
    }
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();
    if (post.shopUsername) {
      window.open(`https://${post.shopUsername}.kalkode.com`, '_blank');
    }
  };
  
  return (
    <PostCard theme={theme} onClick={handleCardClick}>
      <MediaContainer theme={theme}>
        {post.type === 'image' ? (
          <img src={post.contentUrl} alt={post.caption || 'Community post'} />
        ) : (
          <video 
            src={post.contentUrl} 
            loop 
            muted 
            playsInline
            autoPlay
            onMouseEnter={(e) => e.target.play()}
            onMouseLeave={(e) => e.target.pause()}
          />
        )}
      </MediaContainer>
      
      <CaptionBar theme={theme}>
        <LikeRow>
          <LikeButton
            theme={theme}
            isLiked={post.engagement?.likes?.includes(post.currentUserId) || post.isLiked}
            onClick={handleLikeClick}
            disabled={!isAuthenticated}
          >
            <Heart size={16} />
            <span>{post.engagement?.likeCount || 0}</span>
          </LikeButton>
        </LikeRow>
        
        <ShopRow theme={theme}>
          <div className="shop-pfp" onClick={handleProfileClick}>
            {post.shopProfile ? (
              <img src={post.shopProfile} alt={post.shopName} />
            ) : (
              <Store size={10} />
            )}
          </div>
          <span className="shop-name" onClick={handleProfileClick}>
            {post.shopName}
          </span>
          <span className="caption">: {post.caption || 'No caption'}</span>
        </ShopRow>
      </CaptionBar>
    </PostCard>
  );
};

export default CommunityPostCard;